/**
 * Hub demo launcher card.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { ArrowRight, ExternalLink, Route, Server } from "lucide-react";
import React from "react";
import { withAlpha } from "../../design-system/tokens.js";
import { Card } from "../Card.js";
import { CopyButton } from "../CopyButton.js";
import { HighlightText } from "../HighlightText.js";
import { StatusPill } from "../StatusPill.js";
import { isHubReachableView, type HubLauncherDemoView } from "./HubDemoLauncherModel.js";

export interface HubDemoLauncherCardProps {
  readonly view: HubLauncherDemoView;
  readonly query: string;
}

function HubRouteMap({
  accent,
  hubReachable,
}: {
  readonly accent: string;
  readonly hubReachable: boolean;
}): React.JSX.Element {
  const middleLabel = hubReachable ? "Hub route" : "Fixed port";
  const routeNodes = ["Catalog", middleLabel, "Demo"] as const;
  return (
    <div
      aria-label={`Catalog to ${middleLabel.toLowerCase()} to demo`}
      className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 rounded-lg border border-[var(--csk-border)] bg-[color-mix(in_srgb,var(--csk-elevated)_84%,transparent)] p-2.5"
    >
      {routeNodes.map((label, index) => (
        <React.Fragment key={label}>
          {index === 0 ? null : (
            <ArrowRight aria-hidden="true" size={13} className="text-[var(--csk-muted)]" />
          )}
          <span
            className="grid min-h-11 place-items-center rounded-md border bg-[var(--csk-surface)] px-2 text-center text-[11.5px] font-extrabold leading-tight text-[var(--csk-text)]"
            style={{
              borderColor: index === 1 ? withAlpha(accent, "66") : "var(--csk-border)",
              color: index === 1 ? accent : undefined,
            }}
          >
            {label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function relativeLuminance(hex: string): number {
  const channel = (start: number): number => {
    const value = Number.parseInt(hex.slice(start, start + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

function readableTextOn(hex: string): string {
  return relativeLuminance(hex) > 0.45 ? "var(--csk-bright)" : "var(--csk-surface)";
}

export function HubDemoLauncherCard({ view, query }: HubDemoLauncherCardProps): React.JSX.Element {
  const { entry } = view;
  const hubReachable = isHubReachableView(view);
  const ActionIcon = hubReachable ? ArrowRight : ExternalLink;
  const TopologyIcon = hubReachable ? Route : Server;
  const copyLabel = hubReachable ? "Copy route" : "Copy launch task";
  const actionStyle = hubReachable
    ? {
        background: view.accent,
        borderColor: view.accent,
        color: readableTextOn(view.accent),
      }
    : undefined;

  return (
    <Card
      accent={view.accent}
      className="h-full min-h-[276px] border-[var(--csk-border)] bg-[var(--csk-surface)] shadow-[0_10px_24px_rgba(42,50,70,0.08)] backdrop-blur transition duration-150 hover:-translate-y-0.5 hover:border-[var(--csk-accent)] hover:bg-[var(--csk-elevated)] hover:shadow-[0_16px_34px_rgba(42,50,70,0.12)]"
      style={{
        background:
          "linear-gradient(180deg, color-mix(in srgb, var(--csk-surface) 94%, transparent), color-mix(in srgb, var(--csk-elevated) 88%, transparent))",
      }}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-[1_1_12rem]">
            <div className="text-[11.5px] font-bold text-[var(--csk-muted)]">Route surface</div>
            <div className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] font-semibold text-[var(--csk-text)]">
              {hubReachable ? view.actionHref : view.topologyLabel}
            </div>
          </div>
          <CopyButton value={view.command} idleLabel={copyLabel} copiedLabel="Copied" />
        </div>
      }
    >
      <div className="flex h-full flex-col gap-4">
        <header className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border"
            style={{
              background: withAlpha(view.accent, "1F"),
              border: `1px solid ${withAlpha(view.accent, "66")}`,
              color: view.accent,
            }}
          >
            <TopologyIcon size={18} />
          </div>
          <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
            <h2 className="m-0 text-[1rem] font-extrabold leading-snug text-[var(--csk-bright)]">
              <HighlightText text={entry.title} query={query || "CSK"} />
            </h2>
            <StatusPill tone={view.topologyTone} label={view.topologyLabel} />
          </div>
        </header>

        <div className="grid gap-2">
          <p className="m-0 line-clamp-2 text-[12.5px] font-bold leading-snug text-[var(--csk-muted)]">
            {entry.blurb}
          </p>
          <p className="m-0 line-clamp-3 text-sm leading-relaxed text-[var(--csk-text)]">
            {entry.summary}
          </p>
        </div>

        <HubRouteMap accent={view.accent} hubReachable={hubReachable} />

        <a
          href={view.actionHref}
          style={actionStyle}
          className={
            hubReachable
              ? "mt-auto inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border text-[13px] font-extrabold no-underline transition hover:brightness-95"
              : "mt-auto inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border border-[var(--csk-border)] bg-[var(--csk-surface)] text-[13px] font-extrabold text-[var(--csk-text)] no-underline transition hover:bg-[var(--csk-elevated)]"
          }
        >
          <span>{view.actionLabel}</span>
          <ActionIcon aria-hidden="true" size={15} />
        </a>
      </div>
    </Card>
  );
}
